import { AuthPage as AuthPageComponent } from "@/components/auth";
import {
  useSignIn,
  useSignUp,
  useResetPassword,
} from "@/packages/features-auth";
import { getAuthDeepLinkState } from "@/routes/authDeepLink";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

const authSearchSchema = z.object({
  returnTo: z.string().optional(),
  flow: z.string().optional(),
  email: z.string().optional(),
  code: z.string().optional(),
});

export const Route = createFileRoute("/auth")({
  component: AuthRouteComponent,
  validateSearch: authSearchSchema,
});

/**
 * Route component for /auth — wraps AuthPage with returnTo redirect support.
 * After successful login, if returnTo is present in search params, navigate there.
 */
function AuthRouteComponent() {
  const { returnTo, flow: urlFlow, email: urlEmail } = Route.useSearch();
  const {
    flow: initialFlow,
    email: initialEmail,
    code: initialCode,
  } = getAuthDeepLinkState();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useConvexAuth();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (returnTo) {
      navigate({ to: returnTo });
      return;
    }

    navigate({ to: "/" });
  }, [isAuthenticated, isLoading, returnTo, navigate]);

  const resolvedInitialFlow =
    initialFlow ??
    (urlFlow === "reset-otp" && urlEmail
      ? ("reset-otp" as const)
      : urlFlow === "otp-verification" && urlEmail
        ? ("otp-verification" as const)
        : urlFlow === "signUp"
          ? ("signUp" as const)
          : undefined);
  const resolvedInitialEmail = initialEmail ?? urlEmail;

  return (
    <AuthPage
      initialFlow={resolvedInitialFlow}
      initialEmail={
        resolvedInitialFlow === "otp-verification" ||
        resolvedInitialFlow === "reset-otp"
          ? resolvedInitialEmail
          : undefined
      }
      initialResetCode={
        resolvedInitialFlow === "reset-otp" ? initialCode : undefined
      }
      initialOtpCode={
        resolvedInitialFlow === "otp-verification" ? initialCode : undefined
      }
    />
  );
}

type AuthFlow =
  | "signIn"
  | "signUp"
  | "otp-verification"
  | "forgot-password"
  | "reset-otp"
  | "reset-password";

/**
 * AuthPage — standalone auth form component.
 * Exported for use in __root.tsx as fallback for unauthenticated users.
 * Does NOT depend on route context (no Route.useSearch).
 */
export function AuthPage({
  initialFlow,
  initialEmail,
  initialResetCode,
  initialOtpCode,
}: {
  initialFlow?: "otp-verification" | "reset-otp" | "signUp" | "signIn";
  initialEmail?: string;
  initialResetCode?: string;
  initialOtpCode?: string;
} = {}) {
  const { signIn: convexSignIn } = useAuthActions();
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);

  const [flow, setFlow] = useState<AuthFlow>(() => {
    if (initialFlow === "reset-otp" && initialEmail) return "reset-otp";
    if (initialFlow === "otp-verification" && initialEmail)
      return "otp-verification";
    if (initialFlow === "signUp") return "signUp";
    return "signIn";
  });
  // Track which flow triggered OTP so we use the right handlers
  const [otpSource, setOtpSource] = useState<"signIn" | "signUp">("signUp");

  const {
    step: signInStep,
    signIn,
    verifyOtp: signInVerifyOtp,
    resendOtp: signInResendOtp,
    isLoading: signInIsLoading,
    error: signInError,
    verificationEmail: signInVerificationEmail,
    backToCredentials: signInBackToCredentials,
  } = useSignIn();

  const {
    step: signUpStep,
    signUp,
    verifyOtp: signUpVerifyOtp,
    resendOtp: signUpResendOtp,
    isLoading: signUpIsLoading,
    error: signUpError,
    password: signUpPassword,
    setPassword: setSignUpPassword,
    hasAttemptedSubmit: signUpHasAttemptedSubmit,
    verificationEmail: signUpVerificationEmail,
    backToCredentials: signUpBackToCredentials,
  } = useSignUp();

  const {
    step: resetStep,
    requestReset,
    confirmCode,
    setNewPassword,
    resendCode: resetResendCode,
    isLoading: resetIsLoading,
    error: resetError,
    verificationEmail: resetVerificationEmail,
    backToEmail: resetBackToEmail,
    backToOtp: resetBackToOtp,
  } = useResetPassword({
    initialEmail: initialFlow === "reset-otp" ? initialEmail : undefined,
  });

  // Sync sign-up step to flow
  useEffect(() => {
    if (signUpStep === "otp-verification") {
      setOtpSource("signUp");
      setFlow("otp-verification");
    }
  }, [signUpStep]);

  // Sync sign-in step to flow
  useEffect(() => {
    if (signInStep === "otp-verification") {
      setOtpSource("signIn");
      setFlow("otp-verification");
    }
  }, [signInStep]);

  // Sync reset step to flow
  useEffect(() => {
    if (resetStep === "otp") setFlow("reset-otp");
    else if (resetStep === "new-password") setFlow("reset-password");
  }, [resetStep]);

  // Handle deep-link for reset-otp: if code is pre-provided, auto-confirm it
  useEffect(() => {
    if (initialFlow === "reset-otp" && initialEmail && initialResetCode) {
      confirmCode(initialResetCode);
    }
  }, [initialFlow, initialEmail, initialResetCode, confirmCode]);

  const handleSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    await signIn({ email, password });
  };

  const handleSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirm-password") as string;
    const name = formData.get("name") as string | undefined;
    await signUp({ email, password, confirmPassword, name });
  };

  // Route OTP handlers based on which flow triggered it
  const otpVerifyOtp =
    otpSource === "signIn" ? signInVerifyOtp : signUpVerifyOtp;
  const otpResendOtp =
    otpSource === "signIn" ? signInResendOtp : signUpResendOtp;
  const otpIsLoading =
    otpSource === "signIn" ? signInIsLoading : signUpIsLoading;
  const otpError = otpSource === "signIn" ? signInError : signUpError;
  const otpVerificationEmail =
    otpSource === "signIn" ? signInVerificationEmail : signUpVerificationEmail;

  const handleOtpBack = () => {
    if (otpSource === "signIn") {
      signInBackToCredentials();
      setFlow("signIn");
    } else {
      signUpBackToCredentials();
      setFlow("signUp");
    }
  };

  // Forgot-password handlers
  const handleForgotPassword = () => {
    setFlow("forgot-password");
  };

  const handleForgotPasswordSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    await requestReset(email);
  };

  const handleResetOtpSubmit = (code: string) => {
    confirmCode(code);
  };

  const handleResetOtpResend = () => {
    resetResendCode();
  };

  const handleResetOtpBack = () => {
    resetBackToEmail();
    setFlow("forgot-password");
  };

  const handleResetPasswordSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const password = formData.get("newPassword") as string;
    const confirm = formData.get("confirmPassword") as string;
    await setNewPassword(password, confirm);
  };

  const handleResetPasswordBack = () => {
    resetBackToOtp();
    setFlow("reset-otp");
  };

  // Deep-link OTP handlers (used when arriving via email verification link).
  // The "email-verification" flow in convex-auth's password provider validates
  // the OTP code against the authVerificationCodes table and activates the
  // account. If no account exists for this email, the provider throws — caught
  // below and shown as an error.
  const handleDeepLinkOtpVerify = useCallback(
    async (code: string) => {
      if (!initialEmail) return;
      setDeepLinkLoading(true);
      setDeepLinkError(null);
      try {
        const formData = new FormData();
        formData.set("email", initialEmail);
        formData.set("code", code);
        formData.set("flow", "email-verification");
        await convexSignIn("password", formData);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Verification failed";
        setDeepLinkError(message);
      } finally {
        setDeepLinkLoading(false);
      }
    },
    [convexSignIn, initialEmail],
  );

  const handleDeepLinkOtpResend = useCallback(async () => {
    // Without stored credentials, we can't resend from a deep link.
    // Redirect to sign-up flow so user can re-register and get a new code.
    setDeepLinkError(null);
    setFlow("signUp");
  }, []);

  const handleDeepLinkOtpBack = useCallback(() => {
    setFlow("signUp");
  }, []);

  const isDeepLink =
    initialEmail &&
    initialFlow === "otp-verification" &&
    flow === "otp-verification";

  return (
    <AuthPageComponent
      flow={isDeepLink ? "otp-verification" : flow}
      onFlowChange={setFlow}
      brandName="Audinero"
      onSignInSubmit={handleSignInSubmit}
      signInIsLoading={signInIsLoading}
      signInError={signInError}
      onSignUpSubmit={handleSignUpSubmit}
      signUpPassword={signUpPassword}
      onSignUpPasswordChange={setSignUpPassword}
      signUpIsLoading={signUpIsLoading}
      signUpError={signUpError}
      signUpHasAttemptedSubmit={signUpHasAttemptedSubmit}
      onOtpSubmit={isDeepLink ? handleDeepLinkOtpVerify : otpVerifyOtp}
      onOtpResend={isDeepLink ? handleDeepLinkOtpResend : otpResendOtp}
      onOtpBack={isDeepLink ? handleDeepLinkOtpBack : handleOtpBack}
      otpIsLoading={isDeepLink ? deepLinkLoading : otpIsLoading}
      otpError={isDeepLink ? deepLinkError : otpError}
      verificationEmail={isDeepLink ? initialEmail : otpVerificationEmail}
      initialOtpCode={isDeepLink ? initialOtpCode : undefined}
      onForgotPassword={handleForgotPassword}
      onForgotPasswordSubmit={handleForgotPasswordSubmit}
      forgotPasswordIsLoading={resetIsLoading}
      forgotPasswordError={resetError}
      onResetOtpSubmit={handleResetOtpSubmit}
      onResetOtpResend={handleResetOtpResend}
      onResetOtpBack={handleResetOtpBack}
      resetOtpIsLoading={resetIsLoading}
      resetOtpError={resetError}
      resetVerificationEmail={resetVerificationEmail}
      onResetPasswordSubmit={handleResetPasswordSubmit}
      onResetPasswordBack={handleResetPasswordBack}
      resetPasswordIsLoading={resetIsLoading}
      resetPasswordError={resetError}
    />
  );
}
